import React from "react";
import { ArrowRight, Play, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { fmtWeek, fmtMinutes, qaThumb } from "../masterclass/weeklyQa";

// The latest Weekly Q&A, featured. One block: a frame from the recording on
// the left, the questions on the right. It sits at the top of Masterclasses
// while the episode is fresh, and on the dashboard with a dismiss, because
// the whole point of a weekly thing is that people know it happened.
//
// Restrained on purpose: a hairline, the title in Bebas, the questions as a
// list, one button. The questions are the pitch.

export function isFreshQa(row, days = 14) {
  if (!row) return false;
  const t = new Date(row.published_at || `${row.week_of}T12:00:00`).getTime();
  return !Number.isNaN(t) && Date.now() - t < days * 86400000;
}

export function WeeklyQaFeature({ row, C, onOpen, onDismiss, eyebrow = "This week's Q&A", compact = false }) {
  if (!row) return null;
  const qs = (row.questions || []).map(q => q.question).filter(Boolean);
  const shown = qs.slice(0, compact ? 3 : 5);
  const meta = [fmtWeek(row.week_of), fmtMinutes(row.duration_sec),
    `${qs.length} question${qs.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ");
  const thumb = qaThumb(row.mux_playback_id, 960, 540);

  return (
    <div className="bam-qa-feature" role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      style={{ position: "relative", display: "grid",
        gridTemplateColumns: compact ? "minmax(150px, 220px) 1fr" : "minmax(220px, 380px) 1fr",
        gap: 0, borderRadius: 14, overflow: "hidden", cursor: "pointer",
        background: C.cardGradient, border: `1px solid ${C.cardBorder}` }}>
      <div style={{ position: "relative", minHeight: compact ? 120 : 200,
        background: thumb ? `#0b0b0b center/cover url("${thumb}")` : "#0f0f0e" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(0,0,0,0.05), rgba(0,0,0,0.35))" }}/>
        <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span className="bam-qa-play" style={{ width: compact ? 40 : 52, height: compact ? 40 : 52, borderRadius: "50%",
            background: GOLD, display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 22px rgba(0,0,0,0.45)" }}>
            <Play size={compact ? 16 : 20} fill="#111" color="#111" style={{ marginLeft: 3 }}/>
          </span>
        </span>
      </div>

      <div style={{ padding: compact ? "14px 16px 14px" : "20px 24px 20px", minWidth: 0,
        display: "flex", flexDirection: "column", gap: compact ? 8 : 12 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: GOLD }}>
          {eyebrow}{meta ? <span style={{ color: C.textDim, fontWeight: 700 }}> · {meta}</span> : null}
        </div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: compact ? 24 : 32, lineHeight: 0.98,
          letterSpacing: 1.2, color: C.text }}>
          {row.title}
        </div>
        {shown.length > 0 && (
          <div style={{ display: "grid", gap: compact ? 4 : 6 }}>
            {shown.map((q, i) => (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: compact ? 12.5 : 13.5, color: C.textMid,
                lineHeight: 1.4, minWidth: 0 }}>
                <span style={{ color: GOLD, fontWeight: 800, flexShrink: 0 }}>Q</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q}</span>
              </div>
            ))}
            {qs.length > shown.length && (
              <div style={{ fontSize: 11.5, color: C.textDim, paddingLeft: 19 }}>+{qs.length - shown.length} more</div>
            )}
          </div>
        )}
        <div style={{ marginTop: "auto", paddingTop: compact ? 2 : 4, display: "flex", alignItems: "center", gap: 6,
          fontSize: 12.5, fontWeight: 800, color: GOLD }}>
          Watch the answers <ArrowRight size={13}/>
        </div>
      </div>

      {onDismiss && (
        <button className="btn btn-inline" aria-label="Dismiss"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%",
            border: "none", background: "rgba(0,0,0,0.35)", color: C.textDim, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          <X size={13}/>
        </button>
      )}
    </div>
  );
}

export default WeeklyQaFeature;
